/* eslint-disable unicorn/no-null */
import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output
} from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { Category, CategoryInput } from '@generated/base-types';
import { CategoryFragment } from '@new/admin/services/load-dimensions.generated';
import { PoolFormFragment } from '@new/admin/services/load-pool-form.generated';
import { UpdateCategoryMutationVariables } from '@new/admin/services/update-category.generated';
import { FieldComponent } from '@new/common/form/field/field.component';
import { ModalService } from '@new/common/modal/modal.service';
import { FieldErrorsPipe } from '@new/common/pipes/field-errors.pipe';
import { TranslateModule } from '@ngx-translate/core';

@Component({
  selector: 'man-category-form',
  templateUrl: './category-form.component.html',
  host: { class: 'd-block h-100' },
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    FieldComponent,
    TranslateModule,
    FieldErrorsPipe,
    ReactiveFormsModule,
    CommonModule
  ]
})
export class CategoryFormComponent implements OnInit {
  @Input() public category?: CategoryFragment;
  @Input() public categoryNumber?: string;
  @Input() public pool: PoolFormFragment;
  @Input() public set disabled(value: boolean | undefined) {
    value === true ? this.form.disable() : this.form.enable();
  }

  public get disabled(): boolean {
    return this.form.disabled;
  }

  public get edit(): boolean {
    return this.category !== undefined;
  }

  @Output() public create = new EventEmitter<CategoryInput>();
  @Output() public update = new EventEmitter<UpdateCategoryMutationVariables>();
  @Output() public closed = new EventEmitter<CategoryInput>();

  public form = new FormGroup({
    nameDe: new FormControl<Category['nameDe']>(null),
    nameEn: new FormControl<Category['nameEn']>(null),
    nameFr: new FormControl<Category['nameFr']>(null),
    nameIt: new FormControl<Category['nameIt']>(null),
    customIdentifier: new FormControl<Category['customIdentifier']>(null)
  });

  constructor(private readonly modalService: ModalService) {}

  public ngOnInit(): void {
    this.form.patchValue(this.category ?? {});
  }

  public onSave(): void {
    if (!this.form.valid || !this.form.dirty) return;

    this.category === undefined
      ? this.create.emit(this.form.value)
      : this.update.emit({
          id: this.category.id,
          attributes: this.form.value
        });
  }

  public async onClose(): Promise<void> {
    if (this.disabled) return;

    await this.safeClose();
  }

  private async safeClose(): Promise<void> {
    if (this.form.dirty) {
      const discardChanges = await this.modalService.confirmUnsavedChanges();
      if (discardChanges) {
        this.closed.emit();
      }
    } else {
      this.closed.emit();
    }
  }
}
